import { OidcConfiguration, RuntimeOidcConfiguration } from './auth-config.model';

/** Joins an endpoint base with a route, tolerating a slash on either side. */
const endpoint = (base: string, route: string): string =>
  `${base.replace(/\/+$/, '')}/${route.replace(/^\/+/, '')}`;

/**
 * Lays the environment's OIDC settings over the application's own.
 *
 * Only the parts that differ between environments are taken from the deployed
 * file — who the identity provider is, which client we are, and where it sends
 * the browser back. Everything about *how* the flow runs (refresh tokens, the
 * scopes that come with them, silent renew, the routes) stays with the
 * application: those were chosen for the app, not for the environment, and a
 * chart that predates them would quietly turn them off.
 *
 * A field the deployment does not mention keeps its value, so a chart carrying
 * nothing but a client id still works.
 * @param {OidcConfiguration} base The application's own settings.
 * @param {RuntimeOidcConfiguration} runtime What the deployment supplied.
 * @returns {OidcConfiguration} The two merged.
 */
export const mergeRuntimeAuthConfig = (
  base: OidcConfiguration,
  runtime: null | RuntimeOidcConfiguration,
): OidcConfiguration => {
  if (!runtime) return base;

  // `stsServer` is what the charts call it; `authority` is the current name.
  const authority = runtime.authority ?? runtime.stsServer ?? base.authority;
  const wellKnown = runtime.authWellknownEndpoint ?? authority;

  return {
    ...base,
    authority,
    authWellknownEndpoints: {
      authorizationEndpoint:
        runtime.authorizationEndpoint ?? base.authWellknownEndpoints.authorizationEndpoint,
      endSessionEndpoint:
        runtime.endSessionEndpoint ?? base.authWellknownEndpoints.endSessionEndpoint,
      jwksUri: runtime.jwksUri_route
        ? endpoint(wellKnown, runtime.jwksUri_route)
        : base.authWellknownEndpoints.jwksUri,
      tokenEndpoint: runtime.token_route
        ? endpoint(wellKnown, runtime.token_route)
        : base.authWellknownEndpoints.tokenEndpoint,
      userInfoEndpoint: runtime.userinfo_route
        ? endpoint(wellKnown, runtime.userinfo_route)
        : base.authWellknownEndpoints.userInfoEndpoint,
    },
    clientId: runtime.client_id ?? base.clientId,
    clientSecret: runtime.client_secret ?? base.clientSecret,
    postLogoutRedirectUri: runtime.post_logout_redirect_uri
      ? `${window.location.origin}${runtime.post_logout_redirect_uri}`
      : base.postLogoutRedirectUri,
    redirectUrl: runtime.redirect_url
      ? `${window.location.origin}${runtime.redirect_url}`
      : base.redirectUrl,
  };
};
